import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy, where, Timestamp, doc, getDoc } from 'firebase/firestore';

export const CashAccount = () => {
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(0);
  const [dateRange, setDateRange] = useState('month');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchCashTransactions();
  }, [dateRange]);

  const fetchCashTransactions = async () => {
    setLoading(true);
    try {
      let startDate = null;
      const now = new Date();
      switch (dateRange) {
        case 'week':
          startDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          startDate = new Date(now.setMonth(now.getMonth() - 1));
          break;
        case 'year':
          startDate = new Date(now.setFullYear(now.getFullYear() - 1));
          break;
        default:
          startDate = null;
      }

      // Fetch all collections in parallel
      const [paymentsSnapshot, payableSnapshot, clientsSnapshot, ordersSnapshot, pettyCashSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'payments'), orderBy('paymentDate', 'desc'))),
        getDocs(query(collection(db, 'accountsPayable'), orderBy('updatedAt', 'desc'))),
        getDocs(collection(db, 'clients')),
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'pettyCash'))
      ]);

      // Build lookup maps
      const clientsMap = new Map();
      clientsSnapshot.docs.forEach(doc => clientsMap.set(doc.id, { id: doc.id, ...doc.data() }));

      const ordersMap = new Map();
      ordersSnapshot.docs.forEach(doc => ordersMap.set(doc.id, { id: doc.id, ...doc.data() }));

      const allTransactionsArray = [];

      // 1. Inflow: Payments from clients
      for (const paymentDoc of paymentsSnapshot.docs) {
        const payment = { id: paymentDoc.id, ...paymentDoc.data() };
        const paymentDate = payment.paymentDate?.toDate?.() || new Date(payment.paymentDate);
        if (startDate && paymentDate < startDate) continue;

        const client = clientsMap.get(payment.clientId);
        allTransactionsArray.push({
          id: `payment_${payment.id}`,
          date: payment.paymentDate,
          dateObj: paymentDate,
          type: 'inflow',
          description: `Payment received from ${client?.name || 'Unknown'}`,
          partyName: client?.name || 'Unknown',
          partyPhone: client?.phone || '',
          reference: payment.reference || '',
          amount: payment.amount || 0,
        });
      }

      // 2. Outflow: Payments to payables (employees, subcontractors, suppliers)
      for (const payableDoc of payableSnapshot.docs) {
        const payable = { id: payableDoc.id, ...payableDoc.data() };
        if (payable.paymentHistory && payable.paymentHistory.length) {
          for (const payment of payable.paymentHistory) {
            const paymentDate = payment.date?.toDate?.() || new Date(payment.date);
            if (startDate && paymentDate < startDate) continue;

            let typeIcon = '💼';
            if (payable.type === 'employee') typeIcon = '👨‍💼';
            else if (payable.type === 'subcontractor') typeIcon = '👷';
            else if (payable.type === 'supplier') typeIcon = '🏭';

            allTransactionsArray.push({
              id: `payment_out_${payable.id}_${payment.id}`,
              date: payment.date,
              dateObj: paymentDate,
              type: 'outflow',
              description: `${typeIcon} ${payable.type || 'payable'} payment: ${payment.reference || payable.description || 'Payment'}`,
              partyName: payable.name,
              partyPhone: payable.phone || '',
              reference: payment.reference,
              amount: payment.amount || 0,
            });
          }
        }
      }

      // 3. Outflow: Petty cash transactions
      for (const pettyDoc of pettyCashSnapshot.docs) {
        const petty = { id: pettyDoc.id, ...pettyDoc.data() };
        const pettyDate = petty.transactionDate?.toDate?.() || new Date(petty.transactionDate);
        if (startDate && pettyDate < startDate) continue;

        const categoryIcons = {
          transport: '🚛', fuel: '⛽', electricity: '💡', materials: '🪵',
          tools: '🔧', staff_meals: '🍽️', staff_advance: '💰', maintenance: '🔨',
          cleaning: '🧹', office_supplies: '📎', water: '💧', internet: '🌐',
          airtime: '📱', tea: '☕', waste: '🗑️', security: '👮', other: '📋'
        };
        const icon = categoryIcons[petty.category] || '💰';

        allTransactionsArray.push({
          id: `petty_${petty.id}`,
          date: petty.transactionDate,
          dateObj: pettyDate,
          type: 'outflow',
          description: `${icon} Petty Cash: ${petty.description || petty.category}`,
          partyName: petty.payee || 'Petty Cash',
          partyPhone: '',
          reference: petty.reference || '',
          amount: petty.amount || 0,
        });
      }

      // Sort ascending by date (oldest first) to calculate correct running balance
      allTransactionsArray.sort((a, b) => a.dateObj - b.dateObj);

      let runningBalance = 0;
      const transactionsWithBalance = [];
      for (const tx of allTransactionsArray) {
        if (tx.type === 'inflow') runningBalance += tx.amount;
        else runningBalance -= tx.amount;
        transactionsWithBalance.push({ ...tx, balance: runningBalance });
      }

      // Reverse to show newest transactions FIRST
      transactionsWithBalance.reverse();

      setAllTransactions(transactionsWithBalance);
      setTransactions(transactionsWithBalance);
      setBalance(runningBalance);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching cash transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply date filter client‑side (no extra DB calls)
  const applyFilters = useCallback(() => {
    let filtered = [...allTransactions];
    if (dateRange !== 'all') {
      const now = new Date();
      let startDate;
      switch (dateRange) {
        case 'week':
          startDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          startDate = new Date(now.setMonth(now.getMonth() - 1));
          break;
        case 'year':
          startDate = new Date(now.setFullYear(now.getFullYear() - 1));
          break;
        default:
          startDate = null;
      }
      if (startDate) {
        filtered = filtered.filter(t => t.dateObj >= startDate);
      }
    }
    return filtered;
  }, [allTransactions, dateRange]);

  const filteredTransactions = useMemo(() => applyFilters(), [applyFilters]);

  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTransactions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredTransactions, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  const getTotalInflow = () => filteredTransactions.reduce((sum, t) => sum + (t.type === 'inflow' ? t.amount : 0), 0);
  const getTotalOutflow = () => filteredTransactions.reduce((sum, t) => sum + (t.type === 'outflow' ? t.amount : 0), 0);

  if (loading) {
    return React.createElement(
      'div',
      { style: localStyles.loadingContainer },
      React.createElement('div', { style: localStyles.spinner }),
      React.createElement('p', null, 'Loading cash account...')
    );
  }

  return React.createElement(
    'div',
    { style: localStyles.container },
    // Balance Section (unchanged)
    React.createElement(
      'div',
      { style: localStyles.balanceSection },
      React.createElement(
        'div',
        { style: localStyles.balanceMain },
        React.createElement('div', { style: localStyles.balanceIcon }, '💰'),
        React.createElement(
          'div',
          null,
          React.createElement('div', { style: localStyles.balanceLabel }, 'Current Cash Balance'),
          React.createElement('div', { style: localStyles.balanceAmount }, `${balance.toLocaleString()} UGX`)
        )
      ),
      React.createElement(
        'div',
        { style: localStyles.cashStats },
        React.createElement(
          'div',
          { style: localStyles.cashStat },
          React.createElement('div', { style: localStyles.statLabel }, 'Total Inflow'),
          React.createElement('div', { style: { ...localStyles.statValue, ...localStyles.inflow } }, `+${getTotalInflow().toLocaleString()} UGX`)
        ),
        React.createElement(
          'div',
          { style: localStyles.cashStat },
          React.createElement('div', { style: localStyles.statLabel }, 'Total Outflow'),
          React.createElement('div', { style: { ...localStyles.statValue, ...localStyles.outflow } }, `-${getTotalOutflow().toLocaleString()} UGX`)
        ),
        React.createElement(
          'div',
          { style: localStyles.cashStat },
          React.createElement('div', { style: localStyles.statLabel }, 'Net Flow'),
          React.createElement('div', { style: { ...localStyles.statValue, ...(getTotalInflow() - getTotalOutflow() >= 0 ? localStyles.inflow : localStyles.outflow) } }, `${(getTotalInflow() - getTotalOutflow()).toLocaleString()} UGX`)
        )
      )
    ),
    // Filters
    React.createElement(
      'div',
      { style: localStyles.filtersBar },
      React.createElement(
        'div',
        { style: localStyles.filterGroup },
        React.createElement('label', { style: localStyles.filterLabel }, 'Date Range'),
        React.createElement(
          'select',
          { style: localStyles.filterSelect, value: dateRange, onChange: (e) => { setDateRange(e.target.value); setCurrentPage(1); } },
          React.createElement('option', { value: 'week' }, 'Last 7 Days'),
          React.createElement('option', { value: 'month' }, 'Last 30 Days'),
          React.createElement('option', { value: 'year' }, 'Last 12 Months'),
          React.createElement('option', { value: 'all' }, 'All Time')
        )
      ),
      React.createElement(
        'div',
        { style: localStyles.filterGroup },
        React.createElement('label', { style: localStyles.filterLabel }, `Showing ${paginatedTransactions.length} of ${filteredTransactions.length} transactions`)
      )
    ),
    // Transactions Table – newest first
    React.createElement(
      'div',
      { style: localStyles.tableWrapper },
      React.createElement(
        'table',
        { style: localStyles.table },
        React.createElement(
          'thead',
          null,
          React.createElement(
            'tr',
            { style: localStyles.tableHeaderRow },
            React.createElement('th', { style: localStyles.tableHeader }, 'Date'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Type'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Party Name'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Contact'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Description'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Reference'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Money In'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Money Out'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Balance')
          )
        ),
        React.createElement(
          'tbody',
          null,
          paginatedTransactions.map(transaction => {
            const isInflow = transaction.type === 'inflow';
            return React.createElement(
              'tr',
              { key: transaction.id, style: localStyles.tableRow },
              React.createElement('td', { style: localStyles.tableCell }, transaction.dateObj.toLocaleDateString()),
              React.createElement('td', { style: localStyles.tableCell },
                isInflow
                  ? React.createElement('span', { style: { ...localStyles.typeBadge, ...localStyles.inflowBadge } }, '💰 IN')
                  : React.createElement('span', { style: { ...localStyles.typeBadge, ...localStyles.outflowBadge } }, '💸 OUT')
              ),
              React.createElement('td', { style: { ...localStyles.tableCell, ...localStyles.partyName } }, transaction.partyName || '-'),
              React.createElement('td', { style: localStyles.tableCell }, transaction.partyPhone || '-'),
              React.createElement('td', { style: localStyles.tableCell }, transaction.description),
              React.createElement('td', { style: localStyles.tableCell }, transaction.reference || '-'),
              React.createElement('td', { style: { ...localStyles.tableCell, ...localStyles.debitCell } },
                isInflow ? `${transaction.amount.toLocaleString()} UGX` : '-'
              ),
              React.createElement('td', { style: { ...localStyles.tableCell, ...localStyles.creditCell } },
                !isInflow ? `${transaction.amount.toLocaleString()} UGX` : '-'
              ),
              React.createElement('td', { style: { ...localStyles.tableCell, ...localStyles.balanceCell } }, `${transaction.balance.toLocaleString()} UGX`)
            );
          }),
          paginatedTransactions.length === 0 &&
            React.createElement('tr', null, React.createElement('td', { colSpan: '9', style: localStyles.emptyState }, 'No cash transactions found'))
        )
      )
    ),
    // Pagination
    totalPages > 1 &&
      React.createElement(
        'div',
        { style: localStyles.pagination },
        React.createElement(
          'button',
          {
            style: { ...localStyles.pageButton, ...(currentPage === 1 ? localStyles.pageButtonDisabled : {}) },
            onClick: () => setCurrentPage(p => Math.max(1, p - 1)),
            disabled: currentPage === 1
          },
          'Previous'
        ),
        React.createElement('span', { style: localStyles.pageInfo }, `Page ${currentPage} of ${totalPages}`),
        React.createElement(
          'button',
          {
            style: { ...localStyles.pageButton, ...(currentPage === totalPages ? localStyles.pageButtonDisabled : {}) },
            onClick: () => setCurrentPage(p => Math.min(totalPages, p + 1)),
            disabled: currentPage === totalPages
          },
          'Next'
        )
      )
  );
};

// Styles remain exactly as you had them – no changes
const localStyles = {
  container: { padding: '28px' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#64748b' },
  spinner: { width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#7f1a2e', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '16px' },
  balanceSection: { background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', padding: '28px', marginBottom: '28px' },
  balanceMain: { display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '24px' },
  balanceIcon: { fontSize: '48px' },
  balanceLabel: { fontSize: '13px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', marginBottom: '8px' },
  balanceAmount: { fontSize: '36px', fontWeight: '700', color: '#ffffff' },
  cashStats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px', paddingTop: '20px', borderTop: '1px solid #334155' },
  cashStat: { textAlign: 'center' },
  statLabel: { fontSize: '11px', fontWeight: '500', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '8px' },
  statValue: { fontSize: '18px', fontWeight: '600', color: '#ffffff' },
  inflow: { color: '#10b981' },
  outflow: { color: '#ef4444' },
  filtersBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '12px' },
  filterLabel: { fontSize: '13px', fontWeight: '500', color: '#475569' },
  filterSelect: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', background: '#ffffff', cursor: 'pointer' },
  tableWrapper: { overflowX: 'auto', borderRadius: '12px', border: '1px solid #eef2f6' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  tableHeaderRow: { background: '#f8fafc', borderBottom: '1px solid #eef2f6' },
  tableHeader: { textAlign: 'left', padding: '12px 12px', fontWeight: '600', color: '#475569' },
  tableRow: { borderBottom: '1px solid #f1f5f9' },
  tableCell: { padding: '12px 12px', color: '#334155' },
  partyName: { fontWeight: '600', color: '#7f1a2e' },
  typeBadge: { display: 'inline-block', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '500' },
  inflowBadge: { background: '#d1fae5', color: '#059669' },
  outflowBadge: { background: '#fee2e2', color: '#dc2626' },
  debitCell: { color: '#10b981', fontWeight: '600', textAlign: 'right' },
  creditCell: { color: '#ef4444', fontWeight: '600', textAlign: 'right' },
  balanceCell: { fontWeight: '600', color: '#0f172a', textAlign: 'right' },
  emptyState: { textAlign: 'center', padding: '48px', color: '#94a3b8', fontSize: '13px' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '24px', padding: '16px' },
  pageButton: { padding: '8px 16px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  pageInfo: { fontSize: '13px', color: '#64748b' }
};

const styleSheet = document.createElement('style');
styleSheet.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleSheet);

export default CashAccount;